import { api } from "@/lib/api/client";
import type {
  Prospect,
  ActivoRow,
  ValidationModule,
  Task,
  TaskDetail,
  TaskComment,
  TaskAttachment,
  Metrics,
  TeamMember,
  BusinessArea,
  Sprint,
  SprintSegmentTargets,
  ProspectActivity,
  S360Funnel,
} from "./types";

const BASE = "/api/nexus/seguimiento";

export const seguimientoApi = {
  // Equipo
  team: () => api.get(`${BASE}/team`).then((r) => r.data.team as TeamMember[]),
  setTeamRole: (userId: number, role: "operacion" | "direccion") =>
    api.patch(`${BASE}/team/${userId}/role`, { role }).then((r) => r.data),
  // Objetivos del equipo por segmento (artista | productor | proveedor)
  setGoal: (segment: string, target: number) =>
    api.patch(`${BASE}/goals/${segment}`, { target }).then((r) => r.data),

  // Funnel / prospectos
  funnel: (params?: Record<string, unknown>) =>
    api.get(`${BASE}/funnel`, { params }).then((r) => r.data.prospects as Prospect[]),
  createProspect: (body: Record<string, unknown>) =>
    api.post(`${BASE}/prospects`, body).then((r) => r.data.prospect as Prospect),
  updateProspect: (id: number, body: Record<string, unknown>) =>
    api.patch(`${BASE}/prospects/${id}`, body).then((r) => r.data.prospect as Prospect),
  linkProspect: (id: number, body: { userId?: number; email?: string }) =>
    api.post(`${BASE}/prospects/${id}/link`, body).then((r) => r.data),
  deleteProspect: (id: number) =>
    api.delete(`${BASE}/prospects/${id}`).then((r) => r.data),

  // Timeline de actividad del prospecto
  prospectActivities: (id: number) =>
    api
      .get(`${BASE}/prospects/${id}/activities`)
      .then((r) => r.data.activities as ProspectActivity[]),
  addProspectActivity: (id: number, body: { kind: "nota" | "contacto"; body?: string }) =>
    api
      .post(`${BASE}/prospects/${id}/activities`, body)
      .then((r) => r.data.activity as ProspectActivity),

  // Activos (en vivo)
  activos: (params?: Record<string, unknown>) =>
    api.get(`${BASE}/activos`, { params }).then((r) => r.data.activos as ActivoRow[]),

  // Validación de módulos
  validations: (params?: Record<string, unknown>) =>
    api.get(`${BASE}/validations`, { params }).then((r) => r.data.modules as ValidationModule[]),
  createValidation: (body: Record<string, unknown>) =>
    api.post(`${BASE}/validations`, body).then((r) => r.data.validation),
  updateValidation: (id: number, body: Record<string, unknown>) =>
    api.patch(`${BASE}/validations/${id}`, body).then((r) => r.data.validation),
  deleteValidation: (id: number) =>
    api.delete(`${BASE}/validations/${id}`).then((r) => r.data),

  // Tareas (Kanban)
  tasks: (params?: Record<string, unknown>) =>
    api.get(`${BASE}/tasks`, { params }).then((r) => r.data.tasks as Task[]),
  createTask: (body: Record<string, unknown>) =>
    api.post(`${BASE}/tasks`, body).then((r) => r.data.task as Task),
  updateTask: (id: number, body: Record<string, unknown>) =>
    api.patch(`${BASE}/tasks/${id}`, body).then((r) => r.data.task as Task),
  moveTask: (
    id: number,
    body: { status?: string; orderIndex?: number; sprintId?: number | null },
  ) => api.patch(`${BASE}/tasks/${id}/move`, body).then((r) => r.data.task as Task),
  deleteTask: (id: number) => api.delete(`${BASE}/tasks/${id}`).then((r) => r.data),
  taskDetail: (id: number) =>
    api.get(`${BASE}/tasks/${id}`).then((r) => r.data.task as TaskDetail),
  addTaskComment: (id: number, body: { body: string; mentionedUserIds: number[] }) =>
    api.post(`${BASE}/tasks/${id}/comments`, body).then((r) => r.data.comment as TaskComment),
  deleteTaskComment: (commentId: number) =>
    api.delete(`${BASE}/tasks/comments/${commentId}`).then((r) => r.data),
  uploadTaskAttachment: (id: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`${BASE}/tasks/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.attachment as TaskAttachment);
  },
  deleteTaskAttachment: (id: number, path: string) =>
    api.delete(`${BASE}/tasks/${id}/attachments`, { data: { path } }).then((r) => r.data),

  // Áreas de negocio (CRUD + color)
  businessAreas: (params?: Record<string, unknown>) =>
    api
      .get(`${BASE}/business-areas`, { params })
      .then((r) => r.data.businessAreas as BusinessArea[]),
  createBusinessArea: (body: { name: string; colorHex: string; orderIndex?: number }) =>
    api.post(`${BASE}/business-areas`, body).then((r) => r.data.businessArea as BusinessArea),
  updateBusinessArea: (id: number, body: Record<string, unknown>) =>
    api.patch(`${BASE}/business-areas/${id}`, body).then((r) => r.data.businessArea as BusinessArea),
  deleteBusinessArea: (id: number, hard = false) =>
    api
      .delete(`${BASE}/business-areas/${id}`, { params: hard ? { hard: 1 } : undefined })
      .then((r) => r.data),

  // Sprints / Hitos
  sprints: (params?: Record<string, unknown>) =>
    api.get(`${BASE}/sprints`, { params }).then((r) => r.data.sprints as Sprint[]),
  sprintDetail: (id: number) =>
    api.get(`${BASE}/sprints/${id}`).then((r) => r.data as { sprint: Sprint; tasks: Task[] }),
  createSprint: (body: {
    name: string;
    goal?: string | null;
    startDate: string;
    endDate: string;
    segmentTargets?: SprintSegmentTargets;
  }) => api.post(`${BASE}/sprints`, body).then((r) => r.data.sprint as Sprint),
  updateSprint: (id: number, body: Record<string, unknown>) =>
    api.patch(`${BASE}/sprints/${id}`, body).then((r) => r.data.sprint as Sprint),
  closeSprint: (id: number) =>
    api
      .patch(`${BASE}/sprints/${id}/close`)
      .then((r) => r.data as { sprint: Sprint; completed: number; carriedOver: number }),

  // Métricas + alertas
  metrics: () => api.get(`${BASE}/metrics`).then((r) => r.data as Metrics),
  alerts: () => api.get(`${BASE}/alerts`).then((r) => r.data.alerts as Prospect[]),

  // Seguridad 360: embudo de clubes y origen (rutas de seguridad de Nexus)
  s360Funnel: () => api.get(`/api/admin/security/s360/funnel`).then((r) => r.data as S360Funnel),
  setClubOrigin: (clubId: number, source: string | null) =>
    api
      .put(`/api/admin/security/s360/clubs/${clubId}/origin`, { source })
      .then((r) => r.data as { club: { id: number; name: string; acquisitionSource: string | null } }),
};
